import Vue from "vue";
import moment from "moment";
import * as storeHelper from "@/shared/storeHelper";
import * as expenseService from "@/shared/api/expenseService";

import {
    ExpenseEligibility,
    ExpenseUnreimburseGetResponse,
    ExpenseUnreimburseState,
    FetchState,
    UnreimbursedGraphResponse,
    UnreimbursedPage,
    UnreimburseExpense,
    UnreimbursedGraphPoint,
} from "@/shared/types";
import { defineStore } from "pinia";
import _ from "lodash";
import { useExpenseBalanceSummaryStore } from "@/employee/store/expenseBalanceSummary";

export const initialState: ExpenseUnreimburseState = {
    transactionFetchState: FetchState.Uninitialized,
    fetchState: FetchState.Uninitialized,
    countFetchState: FetchState.Uninitialized,
    attachmentsCountFetchState: FetchState.Uninitialized,
    page: 1,
    pageSize: 10,
    numberOfPages: 1,
    unreimbursedExpensesCount: 0,
    unreimbursedWithoutAttachmentsCount: 0,
    records: [],
    graph: null,
    showExpensesWithoutAttachments: false,
};

export const useExpenseUnreimbursedStore = defineStore("expenseUnreimbursed", {
    state: (): ExpenseUnreimburseState => (_.cloneDeep(initialState)),
    getters: {
        getUnreimbursedLineChart: state => state.fetchState === FetchState.Loaded ? state.graph.records : [],
        getUnreimbursedLineChartPoc: state => {
            if (state.fetchState === FetchState.Loaded) {
                return state.graph.records.map(x => {
                    return { balance: x.amount, date: x.date };
                });
            }

            return [];
        },
        graphStartDate: state => state.fetchState === FetchState.Loaded ? state.graph.graphStartDate : "",
        getExpensesList: state => page => {
            if (page <= 0 || page > state.numberOfPages) {
                return [];
            }
            return state.records.slice((page - 1) * state.pageSize, page * state.pageSize);
        },
        hasPages: state => state.numberOfPages > 1,
        currentPage: state => state.page,
        pageCount: state => state.numberOfPages,
        pageRange: state => _.range(state.page - 2, state.page + 3)
            .filter(page => page > 0 && page <= state.numberOfPages),
        isReady: state => state.fetchState === FetchState.Loaded,
        countIsLoaded: state => state.attachmentsCountFetchState === FetchState.Loaded,
        currentPageSelected () {
            return _.every(this.getExpensesList(this.page),
                expense => expense.isSelected);
        },
        totalNumberOfExpenses: state => state.unreimbursedExpensesCount,
        selectedExpenses: state => _.filter(state.records, ex => ex && ex.isSelected),
        selectedExpenseIds () {
            return _.map(<UnreimburseExpense[]>this.selectedExpenses, ex => ex.id);
        },
        selectedTotal () {
            return _.reduce(this.selectedExpenses, (sum, ex) => sum + ex.amount, 0);
        },
        graphCanGoBack: state => state.fetchState === FetchState.Loaded
            && state.countFetchState === FetchState.Loaded
            && moment(state.graph.graphStartDate).isBefore(moment(state.graph.fromDate)),
        graphCanGoForward: state => state.fetchState === FetchState.Loaded
            && state.countFetchState === FetchState.Loaded
            && moment(state.graph.graphEndDate).isAfter(moment(state.graph.toDate)),
        showGraphControls () {
            return this.fetchState === FetchState.Loaded &&
                (this.graphCanGoBack || this.graphCanGoForward);
        },
    },
    actions: {
        setFetchState (s: FetchState) {
            this.fetchState = s;
            setPageCount(this);
        },
        setCountFetchState (s: FetchState) {
            this.countFetchState = s;
            setPageCount(this);
        },
        setAttachmentsCountFetchState (s: FetchState) {
            this.attachmentsCountFetchState = s;
            setPageCount(this);
        },
        setFromResponse (response: ExpenseUnreimburseGetResponse) {
            this.page = response.page;

            let sIndex = (this.page - 1) * this.pageSize;
            const rlen = response.records.length;
            for (let index = 0; index < rlen; index++, sIndex++) {
                Vue.set(this.records, sIndex, response.records[index]);
            }

            const slen = this.records.length;
            for (; sIndex < slen; sIndex++) {
                Vue.delete(this.records, sIndex);
            }
        },
        setUnreimbursedCount (count: number) {
            this.unreimbursedExpensesCount = count;
        },
        setUnreimbursedWithoutAttachmentsCount (count: number) {
            this.unreimbursedWithoutAttachmentsCount = count;
        },
        setGraphFromResponse (response: UnreimbursedGraphResponse) {
            for (const point of response.records) {
                point.date = moment(point.date).format("DD-MMM-YY");
            }

            let graphEndDate = null;
            if (this.graph && this.graph.graphEndDate) {
                graphEndDate = this.graph.graphEndDate;
            }

            if (!graphEndDate) {
                graphEndDate = response.toDate;
            }

            if (response.records.length === 1) {
                const startValue: UnreimbursedGraphPoint = _.clone(response.records[0]);
                startValue.date = moment(response.graphStartDate).format("DD-MMM-YY");
                response.records.splice(0, 0, startValue);
            }

            this.graph = response;
            this.graph.graphEndDate = graphEndDate;
        },
        resetUnreimbursedState () {
            this.fetchState = FetchState.Uninitialized;
            this.numberOfPages = 0;
            this.records = [];
            this.unreimbursedExpensesCount = 0;
            this.graph = null;
        },
        updateUnreimbursedExpense (expense: UnreimburseExpense) {
            if (this.records) {
                const len = this.records.length;
                for (let index = 0; index < len; index++) {
                    const ps = this.records[index];
                    if (ps && ps.id === expense.id) {
                        Vue.set(this.records, index, expense);
                        return;
                    }
                }
            }
        },
        startSwitchMode (mode) {
            this.page = 1;
            this.showExpensesWithoutAttachments = mode;
        },
        stopSwitchMode () {
            const slen = this.records.length;
            for (let i = 0; i < slen; i++) {
                Vue.delete(this.records, i);
            }
        },
        graphMove (back: boolean) {
            if (this.fetchState !== FetchState.Loaded) {
                return Promise.resolve();
            }

            if (back && !this.graphCanGoBack) {
                return Promise.resolve();
            }

            if (!back && !this.graphCanGoForward) {
                return Promise.resolve();
            }

            const startDate = moment(this.graph.fromDate);
            const endDate = moment(this.graph.toDate);

            if (back) {
                startDate.add(-6, "months");
                endDate.add(-6, "months");
            } else {
                startDate.add(6, "months");
                endDate.add(6, "months");
            }

            return expenseService.unreimbursedGraphGet(startDate.format("YYYY-MM-DD"), endDate.format("YYYY-MM-DD"))
                .then(response => {
                    this.setGraphFromResponse(response.data);
                }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        load () {
            if (this.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);
            this.setCountFetchState(FetchState.Loading);
            const endDate = moment();
            const startDate = moment().add(-6, "months");
            expenseService.unreimbursedGetCount().then(r => {
                this.setUnreimbursedCount(r.data);
                this.setCountFetchState(FetchState.Loaded);
            });

            expenseService.unreimbursedGet(this.page, this.pageSize, this.showExpensesWithoutAttachments)
                .then(response => {
                    this.setFromResponse(response.data);
                    return expenseService.unreimbursedGraphGet(startDate.format("YYYY-MM-DD"),
                        endDate.format("YYYY-MM-DD"));
                }).then(response => {
                this.setGraphFromResponse(response.data);
                this.setFetchState(FetchState.Loaded);
            }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadPage (request: UnreimbursedPage) {
            if ((this.fetchState !== FetchState.Loaded || !request || request.page <= 0 || request.page >
                    this.numberOfPages)
                && !request.force) {
                return Promise.resolve();
            }

            const elist = this.getExpensesList(request.page);
            const fullPage = _.every(elist, _.identity);
            if (elist.length && fullPage && !request.force) {
                this.page = request.page;
                return Promise.resolve();
            }
            return expenseService.unreimbursedGet(request.page, this.pageSize, this.showExpensesWithoutAttachments)
                .then(response => {
                    this.setFromResponse(response.data);
                }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        switchMode (request) {
            this.startSwitchMode(request);
            if (request) {
                this.setAttachmentsCountFetchState(FetchState.Loading);
                expenseService.unreimbursedWithoutAttachmentsCount().then(r => {
                    this.setUnreimbursedWithoutAttachmentsCount(r.data);
                    this.setAttachmentsCountFetchState(FetchState.Loaded);
                });
            }
            expenseService.unreimbursedGet(this.page, this.pageSize, this.showExpensesWithoutAttachments)
                .then(response => {
                    this.stopSwitchMode(request);
                    this.setFromResponse(response.data);
                }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        reloadPage () {
            this.loadPage({
                page: this.page,
                force: true
            });
            if (this.showExpensesWithoutAttachments) {
                this.setAttachmentsCountFetchState(FetchState.Loading);
                expenseService.unreimbursedWithoutAttachmentsCount().then(r => {
                    this.setUnreimbursedWithoutAttachmentsCount(r.data);
                    this.setAttachmentsCountFetchState(FetchState.Loaded);
                });
            }
        },
        toggle (expense: UnreimburseExpense) {
            const matchingIndex = this.records.findIndex(ex => ex && ex.id === expense.id);
            if (matchingIndex === -1) {
                return;
            }

            const matchingExpense = this.records[matchingIndex];
            matchingExpense.isSelected = !matchingExpense.isSelected;
            Vue.set(this.records, matchingIndex, matchingExpense);

            const expenseBalanceSummaryStore = useExpenseBalanceSummaryStore();
            if (matchingExpense.isSelected) {
                expenseBalanceSummaryStore.addReimburseMeAmount({
                    amount: expense.amount - expense.reimbursedAmount,
                    expenseId: expense.id,
                    transactionId: 0,
                    eligibility: ExpenseEligibility.Eligible
                });
            } else {
                expenseBalanceSummaryStore.removeReimburseMeAmount(expense.id);
            }
        },
        toggleAll (isSelected: boolean) {
            const len = this.records.length;

            for (let index = 0; index < len; index++) {
                const expense = this.records[index];

                if (expense) {
                    expense.isSelected = isSelected;
                    Vue.set(this.records, index, expense);
                    const expenseBalanceSummaryStore = useExpenseBalanceSummaryStore();
                    if (expense.isSelected) {
                        expenseBalanceSummaryStore.addReimburseMeAmount({
                            amount: expense.amount - expense.reimbursedAmount,
                            expenseId: expense.id,
                            transactionId: 0,
                            eligibility: ExpenseEligibility.Eligible
                        });
                    } else {
                        expenseBalanceSummaryStore.removeReimburseMeAmount(expense.id);
                    }
                }
            }
        }
    }
});

function setPageCount (state) {
    if (state.countFetchState === FetchState.Loaded && state.fetchState === FetchState.Loaded) {
        if (state.showExpensesWithoutAttachments) {
            state.numberOfPages = Math.ceil(state.unreimbursedWithoutAttachmentsCount / state.pageSize);
        } else {
            state.numberOfPages = Math.ceil(state.unreimbursedExpensesCount / state.pageSize);
        }
    }
}
